import { useRef, useState } from 'react';
import { useProfiles } from '../../contexts/ProfilesContext';

// Text input with an @mention autocomplete dropdown.
// Selecting a person inserts "@Name " and records their exact email in `mentions`,
// so notifications target the right person instead of a fuzzy name guess.
export const MentionInput = ({ value, onChange, mentions, setMentions, excludeEmail, onSubmit, placeholder, className }) => {
  const { profiles } = useProfiles();
  const inputRef = useRef(null);
  const [dropdown, setDropdown] = useState(null); // { query, start, index }

  const candidates = dropdown
    ? profiles
        .filter(p => p.is_active && p.email !== excludeEmail &&
          (p.name.toLowerCase().includes(dropdown.query.toLowerCase()) ||
           p.email.toLowerCase().includes(dropdown.query.toLowerCase())))
        .slice(0, 6)
    : [];

  const handleChange = (e) => {
    const text = e.target.value;
    const caret = e.target.selectionStart;
    onChange(text);
    const m = text.slice(0, caret).match(/@(\w*)$/);
    if (m) setDropdown({ query: m[1], start: caret - m[0].length, index: 0 });
    else setDropdown(null);
  };

  const selectPerson = (p) => {
    const before = value.slice(0, dropdown.start);
    const after = value.slice(inputRef.current.selectionStart);
    const token = `@${p.name} `;
    onChange(before + token + after);
    setMentions(prev => prev.some(m => m.email === p.email) ? prev : [...prev, { email: p.email, name: p.name }]);
    setDropdown(null);
    requestAnimationFrame(() => {
      const pos = (before + token).length;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(pos, pos);
    });
  };

  const handleKeyDown = (e) => {
    if (dropdown && candidates.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setDropdown(d => ({ ...d, index: (d.index + 1) % candidates.length })); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setDropdown(d => ({ ...d, index: (d.index - 1 + candidates.length) % candidates.length })); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectPerson(candidates[dropdown.index]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setDropdown(null); return; }
    }
    if (e.key === 'Enter' && onSubmit) onSubmit();
  };

  return (
    <div className="relative flex-1">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setDropdown(null), 150)}
        className={className}
        placeholder={placeholder}
      />
      {dropdown && candidates.length > 0 && (
        <div className="absolute bottom-full mb-1 left-0 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
          {candidates.map((p, i) => (
            <button
              key={p.email}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); selectPerson(p); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${i === dropdown.index ? 'bg-blue-50' : ''}`}
            >
              <span className="font-medium text-slate-800">{p.name}</span>
              <span className="text-xs text-slate-400 ml-2">{p.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
